import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EmployeeAsset, AssetCategory, AssetTransfer } from "@/types/asset";
import { toast } from "sonner";

export const useAssetCategories = () => {
  return useQuery({
    queryKey: ["asset-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_categories")
        .select("*")
        .order("name_ar");
      
      if (error) throw error;
      return data as AssetCategory[];
    },
  });
};

export const useEmployeeAssets = (employeeId?: string) => {
  return useQuery({
    queryKey: ["employee-assets", employeeId],
    queryFn: async () => {
      let query = supabase
        .from("employee_assets")
        .select(`
          *,
          category:asset_categories(*),
          employee:employees!employee_assets_employee_id_fkey(id, name, department, position),
          assigned_by_employee:employees!employee_assets_assigned_by_fkey(id, name)
        `)
        .order("assigned_date", { ascending: false });
      
      if (employeeId) {
        query = query.eq("employee_id", employeeId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as any[];
    },
  });
};

export const useCreateAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (asset: any) => {
      const { data, error } = await supabase
        .from("employee_assets")
        .insert([asset])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-assets"] });
      toast.success("تم إضافة العهدة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء إضافة العهدة");
      console.error(error);
    },
  });
};

export const useUpdateAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<EmployeeAsset> & { id: string }) => {
      const { data, error } = await supabase
        .from("employee_assets")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-assets"] });
      toast.success("تم تحديث العهدة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تحديث العهدة");
      console.error(error);
    },
  });
};

export const useReturnAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      returned_to, 
      return_signature,
      condition,
      notes 
    }: {
      id: string;
      returned_to: string;
      return_signature?: string;
      condition?: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from("employee_assets")
        .update({
          status: "returned",
          actual_return_date: new Date().toISOString(),
          returned_to,
          return_signature,
          condition,
          notes,
        })
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-assets"] });
      toast.success("تم تسليم العهدة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تسليم العهدة");
      console.error(error);
    },
  });
};

export const useTransferAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (transfer: Partial<AssetTransfer>) => {
      // Create transfer record
      const { data: transferData, error: transferError } = await supabase
        .from("asset_transfers")
        .insert([transfer])
        .select()
        .single();
      
      if (transferError) throw transferError;
      
      // Update asset with new employee
      if (transfer.to_employee_id && transfer.asset_id) {
        const { error: updateError } = await supabase
          .from("employee_assets")
          .update({ employee_id: transfer.to_employee_id })
          .eq("id", transfer.asset_id);
        
        if (updateError) throw updateError;
      }
      
      return transferData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-assets"] });
      queryClient.invalidateQueries({ queryKey: ["asset-transfers"] });
      toast.success("تم نقل العهدة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء نقل العهدة");
      console.error(error);
    },
  });
};

export const useAssetTransfers = () => {
  return useQuery({
    queryKey: ["asset-transfers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_transfers")
        .select(`
          *,
          asset:employee_assets(*),
          from_employee:employees!asset_transfers_from_employee_id_fkey(id, name),
          to_employee:employees!asset_transfers_to_employee_id_fkey(id, name),
          approver:employees!asset_transfers_approved_by_fkey(id, name)
        `)
        .order("transfer_date", { ascending: false });
      
      if (error) throw error;
      return data as any[];
    },
  });
};

export const useAssetStats = () => {
  return useQuery({
    queryKey: ["asset-stats"],
    queryFn: async () => {
      const { data: assets, error } = await supabase
        .from("employee_assets")
        .select("status, asset_type");
      
      if (error) throw error;
      
      const stats = {
        total: assets.length,
        assigned: assets.filter(a => a.status === "assigned").length,
        returned: assets.filter(a => a.status === "returned").length,
        lost: assets.filter(a => a.status === "lost").length,
        damaged: assets.filter(a => a.status === "damaged").length,
        physical: assets.filter(a => a.asset_type === "physical").length,
        digital: assets.filter(a => a.asset_type === "digital").length,
        credentials: assets.filter(a => a.asset_type === "credentials").length,
      };
      
      return stats;
    },
  });
};